import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ChatsForDomainTable.scss';
import { ChatForDomainRow } from '../../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { SearchFilter } from '../../../../../shared/components/search-filter/SearchFilter';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import KeywordTags from '../../../../../shared/components/keyword-tags/KeywordTags';
import { PaginationEvent } from '../../../../../shared/helpers/PaginationHelper';
import { Tooltip } from 'primereact/tooltip';
import { KeywordsData } from '../../../../../shared/models/tags/TagsResponse';
import { PROJECTS, QUESTIONS } from '../../../../../shared/routes/path';
import { getModelNameIcon, getModelNameLabel } from '../../../../../shared/helpers/ChatTypeHelpers';

const MAX_VISIBLE_FAVICONS = 4;

interface ChatsForDomainPagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface ChatsForDomainTableProps {
  chats: ChatForDomainRow[];
  pagination?: ChatsForDomainPagination;
  onPageChange: (page: number, pageSize: number) => void;
  isLoading: boolean;
  keyword: string;
  onKeywordChange: (kw: string) => void;
  commonKeywords: KeywordsData[];
  projectId: number;
  hasAdvancedFeatures?: boolean;
}

export const ChatsForDomainTable: React.FC<ChatsForDomainTableProps> = ({
  chats,
  pagination,
  onPageChange,
  isLoading,
  keyword,
  onKeywordChange,
  commonKeywords,
  projectId,
  hasAdvancedFeatures = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const brandsMap = useMemo(() => {
    const map = new Map<string, string>();
    commonKeywords.forEach((k) => map.set(k.normalizedKeyword, k.keyword));
    return map;
  }, [commonKeywords]);

  const first = pagination ? (pagination.page - 1) * pagination.pageSize : 0;
  const rows = pagination?.pageSize ?? 10;
  const totalRecords = pagination?.totalItems ?? chats.length;

  const safeFirst = useMemo(() => {
    if (totalRecords === 0) return 0;
    const maxFirst = Math.floor((totalRecords - 1) / rows) * rows;
    return Math.max(0, Math.min(first, maxFirst));
  }, [first, rows, totalRecords]);

  const handlePageChange = (event: PaginationEvent): void => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    const newPageSize = event.rows;
    if (pagination) {
      if (newPage !== pagination.page || newPageSize !== pagination.pageSize) {
        onPageChange(newPage, newPageSize);
      }
    } else {
      onPageChange(newPage, newPageSize);
    }
  };

  return (
    <div className="stats__panel chats-for-domain-table">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('chatsUsingThisDomain')}
          <CustomTooltip tooltipText="chatsUsingThisDomainTooltip" addMargin={false} />
        </div>
        <div className="page-box__source-filter">
          <SearchFilter value={keyword} onChange={onKeywordChange} className="search-filter--inline" />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="skeleton--table">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          value={chats}
          stripedRows
          tableClassName="table"
          className="table-wrapper table-wrapper--stack chats-for-domain-table__table"
          responsiveLayout="stack"
          tableStyle={{ width: '100%' }}
          paginator
          lazy
          emptyMessage={() => <EmptyPanel />}
          paginatorClassName="pagination"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          rows={rows}
          first={safeFirst}
          onPage={handlePageChange}
          totalRecords={totalRecords}
          rowsPerPageOptions={[10, 25, 50]}
          dataKey="chatId"
          loading={isLoading}
          currentPageReportTemplate={t('rowsNumber') + ':'}
        >
          <Column
            field="promptName"
            header={t('chat')}
            bodyClassName="table__cell--stack-title"
            headerClassName="table__header"
            style={{ minWidth: '280px', width: 'auto' }}
            body={(rowData: ChatForDomainRow, options: any) => {
              const modelIcon = getModelNameIcon(rowData.model);
              const modelIconId = `chat-model-icon-${options.rowIndex}`;
              return (
              <div className="chats-for-domain-table__chat-cell">
                {modelIcon && (
                  <>
                    <span
                      className={`${modelIcon} ico--md chats-for-domain-table__model-icon`}
                      id={modelIconId}
                      data-pr-tooltip={getModelNameLabel(rowData.model)}
                      data-pr-position="top"
                    />
                    <Tooltip target={`#${modelIconId}`} />
                  </>
                )}
                <div className="chats-for-domain-table__chat-content">
                <a
                  className="chats-for-domain-table__chat-name chats-for-domain-table__chat-name--clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    const chatDate = format(new Date(rowData.created), 'yyyy-MM-dd');
                    navigate(`${PROJECTS}/${projectId}${QUESTIONS}/${rowData.monitorPromptId}/daily/all?chatDate=${chatDate}&chatId=${rowData.chatId}`);
                  }}
                >
                  {rowData.promptName}
                </a>
                {rowData.responseSnippet && (
                  <ReactMarkdown
                    className="chats-for-domain-table__chat-snippet text-ellipsis"
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => <>{children}</>,
                      strong: ({ children }) => <>{children}</>,
                      em: ({ children }) => <>{children}</>,
                      a: ({ children }) => <>{children}</>,
                      li: ({ children }) => <>{children}</>,
                      ul: ({ children }) => <>{children}</>,
                      ol: ({ children }) => <>{children}</>,
                      h1: ({ children }) => <>{children}</>,
                      h2: ({ children }) => <>{children}</>,
                      h3: ({ children }) => <>{children}</>,
                      h4: ({ children }) => <>{children}</>,
                      h5: ({ children }) => <>{children}</>,
                      h6: ({ children }) => <>{children}</>,
                      code: ({ children }) => <>{children}</>,
                      pre: ({ children }) => <>{children}</>,
                    }}
                  >
                    {rowData.responseSnippet}
                  </ReactMarkdown>
                )}
                {hasAdvancedFeatures && (
                  <KeywordTags
                    keywordsString={rowData.mentions}
                    keywordsMap={brandsMap}
                    containerWidth={260}
                    minVisibleKeywords={3}
                    uniqueId={`chat-brands-${options.rowIndex}`}
                  />
                )}
                </div>
              </div>
              );
            }}
          />

          <Column
            field="citationCount"
            header={t('citations')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '90px', minWidth: '90px' }}
            body={(rowData: ChatForDomainRow) => rowData.citationCount}
          />

          <Column
            field="sourceDomains"
            header={t('sources')}
            headerClassName="table__header"
            style={{ width: '140px', minWidth: '100px' }}
            body={(rowData: ChatForDomainRow, options: any) => {
              const domains = rowData.sourceDomains ?? [];
              const visible = domains.slice(0, MAX_VISIBLE_FAVICONS);
              const overflow = domains.length - MAX_VISIBLE_FAVICONS;
              const overflowId = `sources-overflow-${options.rowIndex}`;

              return (
                <div className="chats-for-domain-table__sources">
                  {visible.map((d, i) => {
                    const faviconId = `source-favicon-${options.rowIndex}-${i}`;
                    return (
                      <React.Fragment key={i}>
                        <span
                          className="chats-for-domain-table__source-favicon"
                          style={{ backgroundImage: `url("https://favicon.is/${d}")` }}
                          id={faviconId}
                          data-pr-tooltip={d}
                          data-pr-position="top"
                        />
                        <Tooltip target={`#${faviconId}`} />
                      </React.Fragment>
                    );
                  })}
                  {overflow > 0 && (
                    <>
                      <span
                        className="chats-for-domain-table__sources-overflow"
                        data-pr-tooltip={domains.slice(MAX_VISIBLE_FAVICONS).join('\n')}
                        data-pr-position="top"
                        id={overflowId}
                      >
                        +{overflow}
                      </span>
                      <Tooltip target={`#${overflowId}`} />
                    </>
                  )}
                </div>
              );
            }}
          />

          <Column
            field="created"
            header={t('date')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '140px', minWidth: '130px' }}
            body={(rowData: ChatForDomainRow) =>
              format(new Date(rowData.created), 'yyyy-MM-dd')
            }
          />
        </DataTable>
      )}
    </div>
  );
};

export default ChatsForDomainTable;
